/* Personal Graphics in-store assistant — floating chat widget */
(function () {
  "use strict";

  const STORAGE_KEY = "pg_chat_state_v2";
  const NUDGE_KEY = "pg_chat_nudge_seen_v1";
  const PENDING_LOGO_KEY = "pg_ds_pending_logo";
  const MAX_HISTORY = 24;
  const NUDGE_DELAY_MS = 60 * 1000;

  // Human-texting timing. Typing time scales with bubble length so short
  // replies snap fast and long ones get a realistic compose pause. Tuned
  // up from the original values — we specifically wanted slower,
  // less-bot-feeling pacing on initial + follow-up bubbles.
  const READ_PAUSE_MS = 1100;
  const READ_PAUSE_JITTER = 700;
  const MIN_TYPING_MS = 1300;
  const MAX_TYPING_MS = 3400;
  const MS_PER_WORD = 95;
  const INTER_BUBBLE_MS = 1500;
  const INTER_BUBBLE_JITTER = 700;
  const CARD_TYPING_MS = 850;
  const TYPING_OUT_MS = 220;
  const POST_TYPING_GAP_MS = 140;
  // Initial greeting on panel open — slightly slower than before so the
  // customer actually sees the typing dots first, instead of the greeting
  // popping in instantly.
  const GREETING_PRE_MS = 700;
  const GREETING_PRE_JITTER = 500;
  const GREETING_TYPING_MIN = 950;
  const GREETING_TYPING_MAX = 1700;
  // Micro-stutter: small chance per text bubble that the typing dots
  // briefly disappear mid-compose, then come back — looks like Dave
  // paused to think before finishing the sentence.
  const STUTTER_CHANCE = 0.22;
  const STUTTER_HIDE_MS = 320;
  const STUTTER_GAP_MS = 460;
  // Hard cap on a model-emitted <pause seconds="N"> so a malformed value
  // can't strand the customer staring at typing dots forever.
  const PAUSE_MAX_SECONDS = 30;
  // Smart-scroll threshold: if the customer is within this many px of the
  // bottom we keep auto-scrolling; otherwise we leave them where they are
  // and surface a "↓ New message" pill so a new bubble doesn't yank the
  // viewport away from what they're reading.
  const NEAR_BOTTOM_PX = 90;
  // Logo upload limits enforced before we POST to /api/analyze-logo.
  const MAX_LOGO_BYTES = 5 * 1024 * 1024;
  const ALLOWED_LOGO_TYPES = new Set(["image/jpeg", "image/png", "image/gif", "image/webp"]);

  const jitter = (min, max) => min + Math.random() * (max - min);
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  function typingMsFor(piece) {
    if (!piece) return jitter(MIN_TYPING_MS, MIN_TYPING_MS + 200);
    if (piece.kind !== "text") return CARD_TYPING_MS;
    const words = (piece.content || "").trim().split(/\s+/).filter(Boolean).length;
    return clamp(400 + words * MS_PER_WORD, MIN_TYPING_MS, MAX_TYPING_MS);
  }

  class PgChatAssistant extends HTMLElement {
    constructor() {
      super();
      this.endpoint = "";
      this.greeting = "";
      this.nudgeText = "";
      this.avatarUrl = "";
      this.state = { open: false, messages: [] };
      this.streaming = false;
      this.abortController = null;
      // Each stream gets a monotonic id so a queued mid-stream user
      // message can abort the prior renderer cooperatively. The renderer
      // checks `myId === this.streamId` between sleeps and bails out
      // cleanly the instant a new message starts a new stream.
      this.streamId = 0;
      this.nudgeTimer = null;
      this.viewportHandler = null;
      this.scrollLockY = 0;
      // Smart-scroll: track whether the customer is at/near the bottom of
      // the messages. When they scroll up to read, new bubbles do NOT
      // yank them down; instead a "New message" pill appears.
      this.userAtBottom = true;
      this.unseenCount = 0;
      this.scrollListener = null;
      // Logo upload state: while one is uploading we lock the input so a
      // second upload can't double-fire the analyze call mid-flight.
      this.uploadingLogo = false;
      this.pendingLogo = null; // { dataUrl, mediaType, name, analysis }
    }

    connectedCallback() {
      this.endpoint = (this.getAttribute("data-endpoint") || "").replace(/\/$/, "");
      this.greeting =
        this.getAttribute("data-greeting") ||
        "Hi im dave. Can I help you find products, build a quote, or answer any questions?";
      this.nudgeText =
        this.getAttribute("data-nudge") ||
        "Can I help you find something?";
      this.avatarUrl = this.getAttribute("data-avatar") || "";
      // Once the customer has been handed off to Shopify Inbox in this
      // session, Inbox owns the conversation — keep our launcher hidden
      // and let Inbox's launcher stay visible. Otherwise, hide Inbox so
      // the customer only sees one chat (Dave).
      const handedOff = sessionStorage.getItem("pg_chat_handed_off") === "1";
      if (handedOff) {
        document.documentElement.classList.add("pg-chat-handed-off");
      } else {
        document.documentElement.classList.add("pg-inbox-hidden");
      }
      this.loadState();
      this.render();
      this.attachHandlers();
      window.addEventListener("pageshow", () => this.restoreIfOpen());
      this.scheduleNudge();
    }

    loadState() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === "object") {
            this.state.open = !!parsed.open;
            this.state.messages = Array.isArray(parsed.messages)
              ? parsed.messages.slice(-MAX_HISTORY)
              : [];
          }
        }
      } catch (_) {}
      // Pending logo is stored in its own key — kept separate from chat
      // history because the Design Studio reads it on handoff and we don't
      // want a chat-history rotation (slice -24) to wipe it.
      try {
        const rawLogo = sessionStorage.getItem(PENDING_LOGO_KEY);
        if (rawLogo) {
          const parsed = JSON.parse(rawLogo);
          if (parsed && parsed.dataUrl) this.pendingLogo = parsed;
        }
      } catch (_) {}
    }

    saveState() {
      try {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            open: this.state.open,
            messages: this.state.messages.slice(-MAX_HISTORY),
          }),
        );
      } catch (_) {}
    }

    savePendingLogo() {
      try {
        if (this.pendingLogo) {
          sessionStorage.setItem(PENDING_LOGO_KEY, JSON.stringify(this.pendingLogo));
        } else {
          sessionStorage.removeItem(PENDING_LOGO_KEY);
        }
      } catch (_) {}
    }

    render() {
      // Launcher (closed state) keeps the neutral chat-bubble icon — Dave's
      // photo only appears once the customer opens the panel. Avoids a
      // surprise "who is this guy?" moment on first impression.
      const launcherInner = `<svg class="pg-chat-launcher__icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H8l-5 4V6a2 2 0 0 1 2-2Z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
          </svg>`;
      const headerAvatar = this.avatarUrl
        ? `<img class="pg-chat-panel__avatar pg-chat-panel__avatar--img" src="${this.escapeAttr(this.avatarUrl)}" alt="Dave from Personal Graphics" aria-hidden="true">`
        : `<div class="pg-chat-panel__avatar" aria-hidden="true">PG</div>`;
      this.innerHTML = `
        <button class="pg-chat-launcher" type="button" aria-label="Open chat" data-pg-launcher>
          ${launcherInner}
        </button>
        <div class="pg-chat-nudge" data-pg-nudge hidden>
          <button class="pg-chat-nudge__close" type="button" aria-label="Dismiss" data-pg-nudge-close>
            <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          </button>
          <div class="pg-chat-nudge__tail"></div>
        </div>
        <div class="pg-chat-panel" data-pg-panel role="dialog" aria-modal="false" aria-label="Personal Graphics chat" hidden>
          <header class="pg-chat-panel__header">
            <div class="pg-chat-panel__title">
              ${headerAvatar}
              <div class="pg-chat-panel__title-text">
                <span class="pg-chat-panel__eyebrow">Dave from Personal Graphics</span>
                <span class="pg-chat-panel__status">
                  <span class="pg-chat-panel__dot"></span>Usually replies in a few minutes
                </span>
              </div>
            </div>
            <button class="pg-chat-panel__close" type="button" aria-label="Close chat" data-pg-close>
              <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
            </button>
          </header>
          <div class="pg-chat-panel__messages-wrap" data-pg-messages-wrap>
            <div class="pg-chat-panel__messages" data-pg-messages></div>
            <button class="pg-chat-newmsg" type="button" data-pg-newmsg hidden aria-live="polite">
              <span class="pg-chat-newmsg__label" data-pg-newmsg-label>New message</span>
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          </div>
          <form class="pg-chat-panel__input" data-pg-form>
            <input type="file" data-pg-file accept="image/png,image/jpeg,image/gif,image/webp" hidden>
            <button class="pg-chat-panel__attach" type="button" aria-label="Send a logo" data-pg-attach>
              <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M14.5 9.5l-5 5a3.5 3.5 0 1 1-5-5l6-6a2.5 2.5 0 1 1 3.5 3.5l-6 6a1.5 1.5 0 0 1-2-2l5-5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <div class="pg-chat-panel__input-wrap" data-pg-input-wrap>
              <textarea
                class="pg-chat-panel__textarea"
                data-pg-input
                rows="1"
                placeholder="Message"
                aria-label="Message"
              ></textarea>
              <button class="pg-chat-panel__send" type="submit" aria-label="Send">
                <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 13V3M4 7l4-4 4 4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </div>
          </form>
        </div>
      `;

      this.panel = this.querySelector("[data-pg-panel]");
      this.launcher = this.querySelector("[data-pg-launcher]");
      this.closeBtn = this.querySelector("[data-pg-close]");
      this.messagesEl = this.querySelector("[data-pg-messages]");
      this.messagesWrap = this.querySelector("[data-pg-messages-wrap]");
      this.newMsgBtn = this.querySelector("[data-pg-newmsg]");
      this.newMsgLabel = this.querySelector("[data-pg-newmsg-label]");
      this.form = this.querySelector("[data-pg-form]");
      this.input = this.querySelector("[data-pg-input]");
      this.inputWrap = this.querySelector("[data-pg-input-wrap]");
      this.attachBtn = this.querySelector("[data-pg-attach]");
      this.fileInput = this.querySelector("[data-pg-file]");
      this.nudgeEl = this.querySelector("[data-pg-nudge]");
      this.nudgeCloseBtn = this.querySelector("[data-pg-nudge-close]");
      this.nudgeEl.insertAdjacentHTML(
        "afterbegin",
        `<div class="pg-chat-nudge__body">${this.escapeHtml(this.nudgeText)}</div>`,
      );

      if (this.state.open) this.openPanel({ restore: true });
      this.renderMessages();
    }

    attachHandlers() {
      this.launcher.addEventListener("click", () => this.openPanel());
      this.closeBtn.addEventListener("click", () => this.closePanel());
      this.nudgeEl.addEventListener("click", (e) => {
        if (e.target.closest("[data-pg-nudge-close]")) return;
        this.hideNudge(true);
        this.openPanel();
      });
      this.nudgeCloseBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.hideNudge(true);
      });
      this.form.addEventListener("submit", (e) => {
        e.preventDefault();
        this.handleSubmit();
      });
      this.input.addEventListener("keydown", (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          this.handleSubmit();
        }
      });
      this.input.addEventListener("input", () => {
        this.autosize();
        this.syncSendVisibility();
      });
      // Attachment button → triggers the hidden file input. Mobile Safari
      // requires the click to come from a real user gesture; routing it
      // through this button keeps that gesture context intact.
      this.attachBtn.addEventListener("click", () => {
        if (this.uploadingLogo || this.streaming) return;
        this.fileInput.click();
      });
      this.fileInput.addEventListener("change", (e) => {
        const file = e.target.files && e.target.files[0];
        if (file) this.handleLogoFile(file);
        // Reset so selecting the same file twice in a row still fires change.
        e.target.value = "";
      });
      // Scroll position tracking on the messages list. Updates `userAtBottom`
      // so new bubbles can decide whether to auto-scroll or surface the
      // "New message" pill instead.
      this.scrollListener = () => this.handleScroll();
      this.messagesEl.addEventListener("scroll", this.scrollListener, { passive: true });
      this.newMsgBtn.addEventListener("click", () => {
        this.scrollToBottom({ force: true });
        this.hideNewMessagePill();
      });
      // iOS Safari occasionally fails to fire visualViewport resize/scroll
      // events when the soft keyboard opens/closes around focus changes
      // and after submit. Re-applying the handler on focus + after submit
      // keeps the panel pinned to the visible area.
      this.input.addEventListener("focus", () => {
        if (this.viewportHandler) {
          this.viewportHandler();
          setTimeout(() => this.viewportHandler && this.viewportHandler(), 250);
        }
      });
      this.input.addEventListener("blur", () => {
        if (this.viewportHandler) {
          setTimeout(() => this.viewportHandler && this.viewportHandler(), 100);
        }
      });
      // "Chat with Jacob live" button → close our chat and try to open
      // Shopify Inbox's storefront chat. Inbox is a separate widget; we
      // probe a few launcher targets in priority order before falling
      // back to a friendly toast pointing at the bottom-right corner.
      this.messagesEl.addEventListener("click", (e) => {
        const inboxBtn = e.target.closest("[data-pg-open-inbox]");
        if (inboxBtn) {
          e.preventDefault();
          const visitor = {
            name: inboxBtn.getAttribute("data-pg-name") || "",
            email: inboxBtn.getAttribute("data-pg-email") || "",
            phone: inboxBtn.getAttribute("data-pg-phone") || "",
          };
          this.closePanel();
          this.openShopifyInbox(visitor);
          return;
        }
        if (!e.target.closest(".pg-chat-card, .pg-chat-quote__cta")) return;
        this.state.open = false;
        this.saveState();
        this.releaseScrollLock();
        this.detachViewportHandler();
      });
      this.syncSendVisibility();
    }

    /* ---------------- mobile viewport / scroll lock ---------------- */

    isMobileViewport() {
      return window.matchMedia("(max-width: 480px)").matches;
    }

    attachViewportHandler() {
      if (!window.visualViewport) return;
      if (this.viewportHandler) return;
      const vv = window.visualViewport;
      const apply = () => {
        if (!this.isMobileViewport() || !this.panel) {
          if (this.panel) {
            this.panel.style.height = "";
            this.panel.style.transform = "";
          }
          return;
        }
        // Pin the panel to the *visual* viewport — height matches the
        // visible area (which shrinks when the soft keyboard opens) and
        // a Y-translate compensates for any layout-viewport scroll that
        // iOS Safari does to bring a focused input into view. Without
        // the translate, iOS shifts the whole fixed panel above the
        // visible screen when the keyboard opens.
        this.panel.style.height = vv.height + "px";
        if (vv.offsetTop > 0) {
          this.panel.style.transform = `translate3d(0, ${vv.offsetTop}px, 0)`;
        } else {
          // Don't set an identity transform — it would clobber the
          // pg-chat-panel-in open animation. Empty string lets the CSS
          // animation run as authored when offsetTop is 0.
          this.panel.style.transform = "";
        }
        this.scrollToBottom();
      };
      this.viewportHandler = apply;
      vv.addEventListener("resize", apply);
      vv.addEventListener("scroll", apply);
      apply();
    }

    detachViewportHandler() {
      if (!this.viewportHandler || !window.visualViewport) {
        this.viewportHandler = null;
        if (this.panel) {
          this.panel.style.height = "";
          this.panel.style.transform = "";
        }
        return;
      }
      window.visualViewport.removeEventListener("resize", this.viewportHandler);
      window.visualViewport.removeEventListener("scroll", this.viewportHandler);
      this.viewportHandler = null;
      if (this.panel) {
        this.panel.style.height = "";
        this.panel.style.transform = "";
      }
    }

    lockBodyScroll() {
      if (!this.isMobileViewport()) return;
      if (document.documentElement.classList.contains("pg-chat-body-lock")) return;
      this.scrollLockY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = `-${this.scrollLockY}px`;
      // Lock both <html> and <body>. iOS Safari's actual scrolling element
      // varies between themes — locking only one isn't enough on some
      // setups (the page can still drift when the keyboard opens).
      document.documentElement.classList.add("pg-chat-body-lock");
      document.body.classList.add("pg-chat-body-lock");
    }

    releaseScrollLock() {
      if (!document.documentElement.classList.contains("pg-chat-body-lock")) return;
      document.documentElement.classList.remove("pg-chat-body-lock");
      document.body.classList.remove("pg-chat-body-lock");
      document.body.style.top = "";
      window.scrollTo(0, this.scrollLockY || 0);
      this.scrollLockY = 0;
    }

    autosize() {
      this.input.style.height = "auto";
      this.input.style.height = Math.min(120, this.input.scrollHeight) + "px";
    }

    syncSendVisibility() {
      if (!this.inputWrap) return;
      const hasText = this.input.value.trim().length > 0;
      this.inputWrap.classList.toggle("pg-chat-panel__input-wrap--has-text", hasText);
    }

    /* ---------------- engagement nudge ---------------- */

    scheduleNudge() {
      if (this.state.open || this.state.messages.length > 0) return;
      if (sessionStorage.getItem(NUDGE_KEY)) return;
      this.nudgeTimer = setTimeout(() => this.showNudge(), NUDGE_DELAY_MS);
    }

    showNudge() {
      if (this.state.open) return;
      this.nudgeEl.removeAttribute("hidden");
      requestAnimationFrame(() => this.nudgeEl.classList.add("pg-chat-nudge--visible"));
    }

    hideNudge(persist) {
      if (!this.nudgeEl) return;
      this.nudgeEl.classList.remove("pg-chat-nudge--visible");
      setTimeout(() => this.nudgeEl.setAttribute("hidden", ""), 200);
      if (persist) {
        try {
          sessionStorage.setItem(NUDGE_KEY, "1");
        } catch (_) {}
      }
      if (this.nudgeTimer) {
        clearTimeout(this.nudgeTimer);
        this.nudgeTimer = null;
      }
    }

    restoreIfOpen() {
      if (this.state.open && this.panel.hasAttribute("hidden")) {
        this.openPanel({ restore: true });
      }
    }

    openPanel(opts) {
      this.hideNudge(true);
      this.panel.removeAttribute("hidden");
      this.launcher.setAttribute("aria-expanded", "true");
      this.classList.add("pg-chat--open");
      this.state.open = true;
      this.saveState();
      this.lockBodyScroll();
      this.attachViewportHandler();
      if (!opts || !opts.restore) {
        if (this.state.messages.length === 0) {
          this.addAssistantGreeting();
        }
        // Don't auto-focus on mobile — autofocus pops the keyboard
        // immediately, which is jarring before the customer has read the
        // greeting. Desktop still gets focus.
        if (!this.isMobileViewport()) {
          setTimeout(() => this.input.focus(), 150);
        }
      }
      this.scrollToBottom();
    }

    closePanel() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
        this.streaming = false;
      }
      this.panel.setAttribute("hidden", "");
      this.launcher.setAttribute("aria-expanded", "false");
      this.classList.remove("pg-chat--open");
      this.state.open = false;
      this.saveState();
      this.releaseScrollLock();
      this.detachViewportHandler();
    }

    /* ---------------- messages ---------------- */

    addAssistantGreeting() {
      // Greeting is the first thing the customer sees on open. We keep it
      // human-feeling: a short pre-pause so the customer notices the panel
      // open before any motion fires, then the typing dots, then the
      // greeting. Don't snap it in instantly — that's what gave away the
      // bot feel before.
      (async () => {
        await sleep(GREETING_PRE_MS + Math.random() * GREETING_PRE_JITTER);
        this.pushTypingBubble();
        const piece = { kind: "text", content: this.greeting };
        const greetingMs = clamp(typingMsFor(piece), GREETING_TYPING_MIN, GREETING_TYPING_MAX);
        await sleep(greetingMs);
        await this.fadeOutTypingBubble();
        await sleep(POST_TYPING_GAP_MS);
        this.commitPendingBubble(piece);
        const last = this.state.messages[this.state.messages.length - 1];
        if (last) last.isGreeting = true;
        this.saveState();
      })();
    }

    handleSubmit() {
      if (this.uploadingLogo) return;
      const text = this.input.value.trim();
      if (!text) return;
      this.input.value = "";
      this.autosize();
      this.syncSendVisibility();

      // If Dave is mid-reply, abort the in-flight stream cleanly so the
      // customer can queue another message. Bump the streamId so the
      // prior renderer bails on its next tick, then drop any orphan
      // typing bubble it had on screen. Real chats let you keep typing
      // while the other side is typing — same UX here.
      if (this.streaming) {
        this.streamId += 1;
        if (this.abortController) {
          try { this.abortController.abort(); } catch (_) {}
          this.abortController = null;
        }
        this.removeTrailingTypingBubble();
        this.streaming = false;
      }

      // If the customer uploaded a logo earlier in this turn, attach the
      // analysis summary to the message we send to the model so Dave can
      // react to what's actually in the artwork. The image data itself
      // stays in sessionStorage (PENDING_LOGO_KEY) so the Design Studio
      // can pre-load it on handoff — we don't ship the base64 to the API
      // because Sonnet has the analysis already and we save bandwidth.
      let outboundText = text;
      if (this.pendingLogo && this.pendingLogo.analysis && !this.pendingLogo.tagged) {
        const a = this.pendingLogo.analysis;
        const summary = `[Logo image attached — analysis: ${JSON.stringify({
          summary: a.summary,
          recommended_method: a.recommended_method,
          color_count: a.color_count,
          has_gradients: a.has_gradients,
          has_photo: a.has_photo,
          complexity: a.complexity,
          quality: a.quality,
          needs_vector: a.needs_vector,
          suggested_placements: a.suggested_placements,
        })}]`;
        outboundText = `${text}\n\n${summary}`;
        this.pendingLogo.tagged = true;
        this.savePendingLogo();
      }

      this.state.messages.push({
        role: "user",
        segments: [{ kind: "text", content: text, outboundText }],
      });
      this.saveState();
      this.appendLastMessageNode({ force: true });
      this.streamResponse();
      // Re-pin the panel to the visible area after the message renders;
      // iOS layout reflow can drift the visual viewport offsetTop without
      // firing a resize/scroll event, hiding the input behind the keyboard.
      if (this.viewportHandler) {
        this.viewportHandler();
        setTimeout(() => this.viewportHandler && this.viewportHandler(), 50);
        setTimeout(() => this.viewportHandler && this.viewportHandler(), 300);
      }
    }

    /* ---------------- streaming + human timing ---------------- */

    async streamResponse() {
      // Each stream owns a monotonic id. handleSubmit increments
      // this.streamId when a new user message arrives mid-stream, which
      // is the renderer's signal to bail out instead of finishing the
      // previous response on top of the new one.
      this.streamId = (this.streamId || 0) + 1;
      const myStreamId = this.streamId;
      this.streaming = true;
      this.abortController = new AbortController();

      const apiMessages = this.state.messages
        .filter((m) => !m.isGreeting)
        .map((m) => ({
          role: m.role,
          // Prefer outboundText (raw text + injected logo-analysis tag) for
          // user messages so Dave sees the artwork analysis. Falls back to
          // the visible content otherwise.
          content: m.segments
            .map((s) => s.outboundText || s.rawContent || s.content || "")
            .join("")
            .trim(),
        }))
        .filter((m) => m.content.length > 0);

      // Queue of fully-formed paragraphs (or cards) ready to display.
      const paragraphQueue = [];
      let streamEnded = false;
      let rawBuffer = "";

      const enqueueParsed = (raw) => {
        // Split raw paragraph by product-card / quote-handoff tokens.
        const pieces = this.splitBlocks(raw);
        for (const p of pieces) if (p) paragraphQueue.push(p);
      };

      // Start the renderer: a loop that shows a typing indicator, waits, and
      // reveals each queued paragraph as its own bubble. The renderer
      // bails out the moment a newer stream supersedes it (customer
      // sent another message mid-typing).
      const isCancelled = () => this.streamId !== myStreamId;
      const renderer = (async () => {
        // Small "reading" pause before the typing indicator fades in —
        // feels like the person on the other end is reading the message.
        await sleep(READ_PAUSE_MS + Math.random() * READ_PAUSE_JITTER);
        if (isCancelled()) return;
        this.pushTypingBubble();

        // Wait for at least one paragraph before committing anything.
        while (!streamEnded && paragraphQueue.length === 0) {
          if (isCancelled()) return;
          await sleep(80);
        }

        let firstBubble = true;
        while (true) {
          if (isCancelled()) return;
          if (paragraphQueue.length === 0) {
            if (streamEnded) break;
            await sleep(80);
            continue;
          }
          const piece = paragraphQueue.shift();

          // <pause seconds="N"> markers — fade the typing indicator, sit
          // idle for the requested time, then bring the typing indicator
          // back. This is the model's way of saying "I'm researching, hold
          // on" — we use it to space out product-card reveals.
          if (piece.kind === "pause") {
            await this.fadeOutTypingBubble();
            if (isCancelled()) return;
            this.removeTrailingTypingBubble();
            await sleep(piece.seconds * 1000);
            if (isCancelled()) return;
            this.pushTypingBubble();
            continue;
          }

          // Hold the typing indicator for a human-feeling moment. Longer
          // bubbles get a longer "compose" pause; cards snap quicker.
          let typingMs = typingMsFor(piece);
          if (firstBubble) typingMs = Math.min(typingMs, MAX_TYPING_MS * 0.75);
          firstBubble = false;

          // Micro-stutter for text bubbles: small chance the typing dots
          // briefly disappear partway through, like Dave paused to think.
          // Only on text — cards skip the stutter so handoff feels snappy.
          if (piece.kind === "text" && typingMs > 1100 && Math.random() < STUTTER_CHANCE) {
            const firstHalf = typingMs * 0.55;
            await sleep(firstHalf);
            if (isCancelled()) return;
            await this.fadeOutTypingBubble();
            this.removeTrailingTypingBubble();
            await sleep(STUTTER_HIDE_MS + Math.random() * STUTTER_GAP_MS);
            if (isCancelled()) return;
            this.pushTypingBubble();
            await sleep(typingMs - firstHalf);
          } else {
            await sleep(typingMs);
          }
          if (isCancelled()) return;

          // Fade the typing indicator out, then slide the real bubble in.
          await this.fadeOutTypingBubble();
          if (isCancelled()) return;
          await sleep(POST_TYPING_GAP_MS);
          this.commitPendingBubble(piece);

          // If more is coming, let the reader breathe, then bring the next
          // typing indicator in.
          if (paragraphQueue.length > 0 || !streamEnded) {
            await sleep(INTER_BUBBLE_MS + Math.random() * INTER_BUBBLE_JITTER);
            if (isCancelled()) return;
            this.pushTypingBubble();
          }
        }

        // Final typing bubble cleanup if we added one but have nothing to show.
        // Skip if a newer stream took over — handleSubmit already tore the
        // typing bubble down and the new renderer owns the slot now.
        if (!isCancelled()) {
          await this.fadeOutTypingBubble();
          this.removeTrailingTypingBubble();
          this.saveState();
        }
      })();

      try {
        const res = await fetch(this.endpoint + "/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ messages: apiMessages }),
          signal: this.abortController.signal,
        });
        if (!res.ok || !res.body) throw new Error("Network error");

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let sseBuffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          sseBuffer += decoder.decode(value, { stream: true });
          const parts = sseBuffer.split("\n\n");
          sseBuffer = parts.pop() || "";
          for (const part of parts) {
            const delta = this.parseSSEChunk(part);
            if (delta == null) continue;
            rawBuffer += delta;

            // Flush completed paragraphs (anything before a \n\n).
            let idx;
            while ((idx = rawBuffer.indexOf("\n\n")) !== -1) {
              const paragraph = rawBuffer.slice(0, idx).trim();
              rawBuffer = rawBuffer.slice(idx + 2);
              if (paragraph) enqueueParsed(paragraph);
            }
          }
        }

        // Flush whatever's left.
        const tail = rawBuffer.trim();
        if (tail) enqueueParsed(tail);
      } catch (err) {
        if (err.name !== "AbortError") {
          paragraphQueue.push({
            kind: "text",
            content:
              "Sorry, I couldn't connect just now. For urgent questions, email jacob@personalgraphicsonline.com or call (231) 347-6347.",
          });
        }
      } finally {
        streamEnded = true;
        await renderer;
        // Only clear shared instance state if WE still own it. A newer
        // stream may have superseded us (mid-stream user message) — in
        // that case it owns this.streaming + this.abortController, and
        // we leave them alone.
        if (this.streamId === myStreamId) {
          this.streaming = false;
          this.abortController = null;
        }
      }
    }

    parseSSEChunk(chunk) {
      const lines = chunk.split("\n");
      let event = "message";
      let data = "";
      for (const line of lines) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data = line.slice(5).trim();
      }
      if (!data) return null;
      let payload;
      try {
        payload = JSON.parse(data);
      } catch (_) {
        return null;
      }
      if (event === "text" && typeof payload.delta === "string") {
        return payload.delta;
      }
      if (event === "error") {
        return "\n\n" + (payload.message || "Something went wrong.") + "\n\n";
      }
      return null;
    }

    // Split a raw paragraph string into pieces by product-card,
    // quote-handoff, human-handoff, callback-card, and pause markers.
    // Each piece becomes its own bubble (or its own delay, in the case
    // of a pause). Pause markers don't produce a visible bubble.
    splitBlocks(raw) {
      const pieces = [];
      const re = /<(product-card|quote-handoff|human-handoff|callback-card)>([\s\S]*?)<\/\1>|<pause\s+seconds=["']?(\d+)["']?\s*\/?\s*>(?:\s*<\/pause>)?/g;
      let last = 0;
      let m;
      while ((m = re.exec(raw)) !== null) {
        const before = raw.slice(last, m.index).trim();
        if (before) pieces.push({ kind: "text", content: before });
        if (m[3] !== undefined) {
          // <pause seconds="N"> — emit a pause piece, capped to PAUSE_MAX_SECONDS
          const secs = clamp(parseInt(m[3], 10) || 0, 0, PAUSE_MAX_SECONDS);
          if (secs > 0) pieces.push({ kind: "pause", seconds: secs });
        } else {
          try {
            const data = JSON.parse(m[2].trim());
            const tag = m[1];
            const kind =
              tag === "product-card" ? "product" :
              tag === "quote-handoff" ? "quote" :
              tag === "human-handoff" ? "human" :
              "callback";
            pieces.push({ kind, data });
          } catch (_) {
            // Swallow malformed card JSON silently.
          }
        }
        last = m.index + m[0].length;
      }
      const after = raw.slice(last).trim();
      if (after) pieces.push({ kind: "text", content: after });
      return pieces;
    }

    /* ---------------- logo upload ---------------- */

    async handleLogoFile(file) {
      if (this.uploadingLogo || this.streaming) return;
      if (!ALLOWED_LOGO_TYPES.has(file.type)) {
        this.toast("That file type isn't supported. Try PNG, JPG, GIF, or WEBP.");
        return;
      }
      if (file.size > MAX_LOGO_BYTES) {
        this.toast("That image is over 5MB — try a smaller version.");
        return;
      }
      this.uploadingLogo = true;
      this.attachBtn.classList.add("pg-chat-panel__attach--busy");

      // Read as data URL for both preview AND the eventual sessionStorage
      // handoff to Design Studio. Base64 (without the prefix) goes to the
      // analyze endpoint.
      let dataUrl = "";
      try {
        dataUrl = await readFileAsDataUrl(file);
      } catch (_) {
        this.toast("Couldn't read that file.");
        this.uploadingLogo = false;
        this.attachBtn.classList.remove("pg-chat-panel__attach--busy");
        return;
      }

      // Drop the image bubble into the chat immediately so the customer
      // sees their upload landed before Dave's reply starts cooking.
      // ObjectURL renders instantly in the IMG tag (no base64 → image
      // decode penalty), and the data URL goes ONLY into pendingLogo for
      // the Design Studio handoff. We don't write a 4MB data URL into
      // chat-history sessionStorage at all.
      let objectUrl = "";
      try {
        objectUrl = URL.createObjectURL(file);
      } catch (_) { objectUrl = dataUrl; }

      this.state.messages.push({
        role: "user",
        segments: [{
          kind: "image",
          objectUrl,
          fileName: file.name || "logo",
          mediaType: file.type,
        }],
      });
      this.saveState();
      this.appendLastMessageNode({ force: true });

      // Show a loading typing-style indicator while we hit the vision API.
      // The pushTypingBubble path is reused so the cleanup at the end is
      // identical to a normal Dave reply.
      this.pushTypingBubble();

      const base64 = dataUrl.includes(",") ? dataUrl.slice(dataUrl.indexOf(",") + 1) : dataUrl;
      let analysis = null;
      try {
        const res = await fetch(this.endpoint + "/api/analyze-logo", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ image_base64: base64, media_type: file.type }),
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok && json.ok && json.analysis) analysis = json.analysis;
      } catch (e) {
        // Network failure — fall through; we still want to drop a message
        // to Dave so the conversation continues even without analysis.
      }

      // Save the logo for Design Studio handoff regardless of whether the
      // analysis succeeded — DS only needs the image. Add a tiny version
      // marker so we can invalidate this format later if we change shape.
      this.pendingLogo = {
        v: 1,
        dataUrl,
        mediaType: file.type,
        name: file.name || "logo",
        analysis: analysis || null,
        createdAt: Date.now(),
        tagged: false,
      };
      this.savePendingLogo();

      // Tear the typing indicator back down so streamResponse can put its
      // own one back — keeps the lifecycle clean.
      await this.fadeOutTypingBubble();
      this.removeTrailingTypingBubble();

      // Build the synthetic outbound text that the model sees. The image
      // itself isn't sent to the chat API (that's a separate vision call
      // we already made above) — Dave just sees the analysis JSON.
      const outboundText = analysis
        ? `[Customer just uploaded a logo for the project.]\n\n[Logo image attached — analysis: ${JSON.stringify(analysis)}]`
        : `[Customer just uploaded a logo for the project. The vision analysis failed; ask them to describe it briefly so you can recommend a method.]`;

      // Push a synthetic user message that the model receives but the
      // customer's view doesn't show again (the image bubble already
      // covered it). We mark `hidden: true` so renderMessage skips it,
      // but apiMessages still picks up its outboundText.
      this.state.messages.push({
        role: "user",
        hidden: true,
        segments: [{ kind: "text", content: "", outboundText }],
      });
      this.saveState();

      this.uploadingLogo = false;
      this.attachBtn.classList.remove("pg-chat-panel__attach--busy");

      // Now trigger Dave's reply just like a normal message.
      this.streamResponse();
    }

    toast(text) {
      let toast = document.querySelector(".pg-chat-inbox-toast");
      if (toast) toast.remove();
      toast = document.createElement("div");
      toast.className = "pg-chat-inbox-toast";
      toast.textContent = text;
      document.body.appendChild(toast);
      requestAnimationFrame(() => toast.classList.add("pg-chat-inbox-toast--visible"));
      setTimeout(() => {
        toast.classList.remove("pg-chat-inbox-toast--visible");
        setTimeout(() => toast.remove(), 300);
      }, 4000);
    }

    /* ---------------- bubble state helpers ---------------- */

    /**
     * Append the just-pushed last message as a single new DOM node, instead
     * of re-rendering the entire list. Re-rendering caused every prior
     * bubble to replay its entrance animation and yanked scroll on every
     * tick — we specifically called this out as feeling "the whole
     * chat moves" each time a new message arrives.
     */
    appendLastMessageNode(opts) {
      const idx = this.state.messages.length - 1;
      if (idx < 0 || !this.messagesEl) return;
      const html = this.renderMessage(this.state.messages[idx], this.seenContextBeforeLast());
      if (!html) return;
      const tmpl = document.createElement("template");
      tmpl.innerHTML = html.trim();
      const node = tmpl.content.firstElementChild;
      if (!node) return;
      this.messagesEl.appendChild(node);
      this.scrollToBottom(opts);
    }

    /**
     * Replace the trailing DOM node with a fresh render of the last message.
     * Used when a typing bubble commits to a real bubble — the slot stays,
     * just its content swaps in place, so prior bubbles never re-animate.
     */
    replaceLastMessageNode(opts) {
      const idx = this.state.messages.length - 1;
      if (idx < 0 || !this.messagesEl) return;
      const html = this.renderMessage(this.state.messages[idx], this.seenContextBeforeLast());
      const last = this.messagesEl.lastElementChild;
      if (!html) {
        if (last) this.messagesEl.removeChild(last);
        return;
      }
      const tmpl = document.createElement("template");
      tmpl.innerHTML = html.trim();
      const node = tmpl.content.firstElementChild;
      if (!node) return;
      if (last) this.messagesEl.replaceChild(node, last);
      else this.messagesEl.appendChild(node);
      this.scrollToBottom(opts);
    }

    removeLastMessageNode() {
      if (this.messagesEl && this.messagesEl.lastElementChild) {
        this.messagesEl.removeChild(this.messagesEl.lastElementChild);
      }
    }

    /**
     * Build the dedupe context from every message EXCEPT the last one
     * (which is the one we're rendering). Tracks BOTH:
     *   - quote signatures (handle + qty + color + method) so a
     *     re-emitted quote-handoff card doesn't show twice.
     *   - product handles so the model can't loop the customer through
     *     the same Port & Co Pique Polo three times in one conversation.
     *
     * The system prompt is the first line of defense; this is the safety
     * net for when Sonnet ignores it (which has happened in production).
     */
    seenContextBeforeLast() {
      const quotes = new Set();
      const products = new Set();
      for (let i = 0; i < this.state.messages.length - 1; i++) {
        const m = this.state.messages[i];
        if (m.role !== "assistant" || !m.segments) continue;
        const seg = m.segments[0];
        if (!seg) continue;
        if (seg.kind === "quote") {
          const d = seg.data || {};
          const sig = [d.product_handle || "", d.quantity ?? "", d.color || "", d.decoration_method || ""].join("|");
          if (sig) quotes.add(sig);
        } else if (seg.kind === "product") {
          const handle = (seg.data && seg.data.handle) || "";
          if (handle) products.add(handle);
        }
      }
      return { quotes, products };
    }

    lastAssistantPending() {
      for (let i = this.state.messages.length - 1; i >= 0; i--) {
        const m = this.state.messages[i];
        if (m.role === "assistant" && m.pendingTyping) return m;
      }
      return null;
    }

    pushTypingBubble() {
      this.state.messages.push({
        role: "assistant",
        segments: [],
        pendingTyping: true,
      });
      // Typing dots aren't real content — never trigger the "New message"
      // pill on them. silent:true skips the unread counter increment.
      this.appendLastMessageNode({ silent: true });
    }

    async fadeOutTypingBubble() {
      if (!this.messagesEl) return;
      const nodes = this.messagesEl.querySelectorAll(".pg-chat-msg--typing");
      const el = nodes[nodes.length - 1];
      if (!el) return;
      el.classList.add("pg-chat-msg--typing-out");
      await sleep(TYPING_OUT_MS);
    }

    commitPendingBubble(piece) {
      // Replace the most recent typing bubble with real content. This is
      // the moment a "new message" actually arrives — if the customer is
      // scrolled up, the pill should appear so they know without their
      // viewport getting yanked.
      const pending = this.lastAssistantPending();
      if (!pending) {
        this.state.messages.push({
          role: "assistant",
          segments: [piece],
        });
        this.appendLastMessageNode();
        return;
      }
      pending.segments = [piece];
      delete pending.pendingTyping;
      this.replaceLastMessageNode();
    }

    removeTrailingTypingBubble() {
      const last = this.state.messages[this.state.messages.length - 1];
      if (last && last.pendingTyping) {
        this.state.messages.pop();
        this.removeLastMessageNode();
      }
    }

    /* ---------------- rendering ---------------- */

    /**
     * Full DOM rebuild — only used by `render()` on initial mount and on
     * sessionStorage state restore. Hot-path message updates go through
     * appendLastMessageNode / replaceLastMessageNode / removeLastMessageNode
     * so prior bubbles never replay their entrance animation and scroll
     * doesn't jump on every stream tick.
     */
    renderMessages() {
      const ctx = { quotes: new Set(), products: new Set() };
      this.messagesEl.innerHTML = this.state.messages
        .map((m) => this.renderMessage(m, ctx))
        .join("");
      // Initial mount: force scroll to bottom so the customer sees the
      // most recent message. After this point, smart-scroll takes over
      // and respects the customer's scroll position.
      this.scrollToBottom({ force: true });
    }

    renderMessage(m, ctx) {
      // Back-compat: callers used to pass a Set of quote sigs. Coerce to
      // the new shape so old call sites still work.
      if (ctx instanceof Set) ctx = { quotes: ctx, products: new Set() };
      ctx = ctx || { quotes: new Set(), products: new Set() };
      // Hidden synthetic messages (e.g. the logo-analysis hint we send
      // to the model after an image upload) ship to the API but never
      // render in the customer's view — the image bubble already
      // covered the visual "I uploaded my logo" beat.
      if (m.hidden) return "";
      if (m.role === "user") {
        // A logo upload from the customer renders as an image bubble.
        // Object URL renders instantly; data URL is a fallback (only
        // present after a sessionStorage round-trip on the same tab).
        const parts = m.segments
          .map((s) => {
            if (s.kind === "image") {
              const src = this.escapeAttr(s.objectUrl || s.dataUrl || "");
              const name = this.escapeHtml(s.fileName || "Logo");
              if (!src) return "";
              return `
                <div class="pg-chat-msg__image">
                  <span class="pg-chat-msg__image-label">
                    <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M8 4l-4 4a2 2 0 1 1-3-3l5-5a3 3 0 1 1 4 4l-5 5" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    <span class="pg-chat-msg__image-name">${name}</span>
                  </span>
                  <span class="pg-chat-msg__image-frame">
                    <img src="${src}" alt="${this.escapeAttr(s.fileName || "Logo")}">
                  </span>
                </div>
              `;
            }
            const text = s.content || "";
            if (!text) return "";
            return `<div class="pg-chat-msg__bubble">${this.formatText(text)}</div>`;
          })
          .filter(Boolean)
          .join("");
        return `<div class="pg-chat-msg pg-chat-msg--user">${parts}</div>`;
      }
      // Assistant: could be typing, or one segment (text, product, or quote).
      if (m.pendingTyping) {
        return `
          <div class="pg-chat-msg pg-chat-msg--assistant pg-chat-msg--typing">
            <div class="pg-chat-msg__bubble">
              <span class="pg-chat-typing"><span></span><span></span><span></span></span>
            </div>
          </div>
        `;
      }
      const seg = m.segments[0];
      if (!seg) return "";
      if (seg.kind === "text") {
        return `
          <div class="pg-chat-msg pg-chat-msg--assistant">
            <div class="pg-chat-msg__bubble">${this.formatText(seg.content)}</div>
          </div>
        `;
      }
      if (seg.kind === "product") {
        // Suppress product-card duplicates by handle. Sonnet sometimes
        // re-emits the same card after the customer adds a qualifier
        // (qty, color refinement) — the system prompt + dynamic stage
        // reminder both forbid this, but the safety net catches stragglers
        // so the customer never sees the same card twice.
        const handle = (seg.data && seg.data.handle) || "";
        if (handle && ctx.products && ctx.products.has(handle)) return "";
        if (handle && ctx.products) ctx.products.add(handle);
        const card = this.renderProductCard(seg.data);
        return card
          ? `<div class="pg-chat-msg pg-chat-msg--assistant pg-chat-msg--card">${card}</div>`
          : "";
      }
      if (seg.kind === "quote") {
        const data = seg.data || {};
        const sig = [
          data.product_handle || "",
          data.quantity ?? "",
          data.color || "",
          data.decoration_method || "",
        ].join("|");
        if (sig && ctx.quotes && ctx.quotes.has(sig)) return "";
        if (sig && ctx.quotes) ctx.quotes.add(sig);
        const card = this.renderQuoteHandoff(data);
        return card
          ? `<div class="pg-chat-msg pg-chat-msg--assistant pg-chat-msg--quote">${card}</div>`
          : "";
      }
      if (seg.kind === "human") {
        const card = this.renderHumanHandoff(seg.data);
        return card
          ? `<div class="pg-chat-msg pg-chat-msg--assistant pg-chat-msg--quote">${card}</div>`
          : "";
      }
      if (seg.kind === "callback") {
        const card = this.renderCallbackCard(seg.data);
        return card
          ? `<div class="pg-chat-msg pg-chat-msg--assistant pg-chat-msg--quote">${card}</div>`
          : "";
      }
      return "";
    }

    renderProductCard(data) {
      if (!data || !data.handle || !data.title) return "";
      const href = "/products/" + encodeURIComponent(data.handle);
      const img = data.image
        ? `<img class="pg-chat-card__image" src="${this.escapeAttr(data.image)}" alt="${this.escapeAttr(data.title)}" loading="lazy">`
        : `<div class="pg-chat-card__image pg-chat-card__image--empty"></div>`;
      const colors = Array.isArray(data.colors) && data.colors.length
        ? `<div class="pg-chat-card__colors">${this.escapeHtml(data.colors.slice(0, 3).join(" · "))}${data.colors.length > 3 ? " +" + (data.colors.length - 3) : ""}</div>`
        : "";
      const price = data.price
        ? `<div class="pg-chat-card__price">${this.escapeHtml(data.price)}</div>`
        : "";
      return `
        <a class="pg-chat-card" href="${href}" aria-label="View ${this.escapeAttr(data.title)}">
          ${img}
          <div class="pg-chat-card__body">
            <div class="pg-chat-card__title">${this.escapeHtml(data.title)}</div>
            ${price}
            ${colors}
          </div>
          <div class="pg-chat-card__cta" aria-hidden="true">
            <svg viewBox="0 0 16 16"><path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </div>
        </a>
      `;
    }

    renderQuoteHandoff(data) {
      if (!data || !data.product_handle || !data.quantity) return "";

      // Prefer the server-built handoff_url (already routed to the correct
      // start-your-X page based on product category). Fall back to the
      // general project inquiry page if an older response didn't include one.
      let href = data.handoff_url;
      if (!href || typeof href !== "string" || href[0] !== "/") {
        const params = new URLSearchParams();
        params.set("pg_quote", "1");
        params.set("pg_handle", String(data.product_handle));
        if (data.product_title) params.set("pg_title", String(data.product_title));
        params.set("pg_qty", String(data.quantity));
        if (data.color) params.set("pg_color", String(data.color));
        if (data.decoration_method) params.set("pg_method", String(data.decoration_method));
        if (data.notes) params.set("pg_notes", String(data.notes).slice(0, 120));
        href = "/pages/start-your-project?" + params.toString();
      }

      // If the customer uploaded a logo earlier in the conversation, rewrite
      // the handoff URL so Design Studio drops them straight into the Logo
      // mode (instead of the entry-screen Choice). LogoMode reads the same
      // sessionStorage key on mount and pre-loads the file onto the canvas.
      if (this.pendingLogo && href) {
        try {
          href = href.replace(/(\?|&)ds_start=choice(?=&|#|$)/, "$1ds_start=logo");
        } catch (_) {}
      }

      // Persist quote to sessionStorage so the inquiry form can pick it up.
      try {
        sessionStorage.setItem("pg_chat_pending_quote", JSON.stringify(data));
      } catch (_) {}

      const title = this.escapeHtml(data.product_title || data.product_handle);
      const estimate = data.estimate || {};

      // Build the decoration sub-label (e.g. "Screen Print · 2 colors", "Embroidery · chest")
      let decorationLabel = data.decoration_method || "";
      if (data.decoration_method === "Screen Print" && data.print_colors) {
        decorationLabel = `${data.decoration_method} · ${data.print_colors} color${data.print_colors > 1 ? "s" : ""}`;
      } else if (data.decoration_method === "Embroidery" && data.print_location) {
        decorationLabel = `Embroidery · ${data.print_location === "full_back" ? "full back" : "chest / hat"}`;
      } else if (data.decoration_method === "DTF" && data.print_size) {
        decorationLabel = `DTF · ${data.print_size === "full" ? "full side" : "up to 4×4\""}`;
      }

      const rows = [
        ["Quantity", data.quantity],
        ["Color", data.color],
        ["Decoration", decorationLabel],
      ]
        .filter(([, v]) => v != null && v !== "")
        .map(
          ([k, v]) =>
            `<li><span>${this.escapeHtml(k)}</span><strong>${this.escapeHtml(String(v))}</strong></li>`,
        )
        .join("");

      // Pick headline total: decorated if the tool computed it, otherwise fall
      // back to the old blank-only field for backward-compat.
      const headlineTotal = estimate.line_total_decorated || estimate.line_total_blank;
      const headlinePer   = estimate.per_piece_decorated || estimate.per_piece_blank;

      const breakdownRows = [];
      if (estimate.garment_line_total && estimate.print_line_total && estimate.print_line_total !== "—") {
        breakdownRows.push(
          `<div class="pg-chat-quote__breakdown-row"><span>Garment${estimate.discount_label ? ` · ${this.escapeHtml(estimate.discount_label)}` : ""}</span><span>${this.escapeHtml(estimate.garment_line_total)}</span></div>`,
        );
        breakdownRows.push(
          `<div class="pg-chat-quote__breakdown-row"><span>Print</span><span>${this.escapeHtml(estimate.print_line_total)}</span></div>`,
        );
      }

      const qtyLabel = data.quantity
        ? `${data.quantity} ${Number(data.quantity) === 1 ? "pc" : "pcs"}`
        : "";

      const priceBlock = (headlinePer || headlineTotal)
        ? `
          <div class="pg-chat-quote__price">
            <div class="pg-chat-quote__price-main">
              <span>Per piece</span>
              <strong>${this.escapeHtml(headlinePer || headlineTotal)}</strong>
            </div>
            ${
              headlineTotal && headlinePer
                ? `<div class="pg-chat-quote__price-sub">${this.escapeHtml(headlineTotal)} total${qtyLabel ? ` for ${qtyLabel}` : ""}</div>`
                : ""
            }
            ${
              breakdownRows.length
                ? `<div class="pg-chat-quote__breakdown">${breakdownRows.join("")}</div>`
                : ""
            }
            <div class="pg-chat-quote__price-note">${this.escapeHtml(estimate.note || "Final pricing confirmed at proof.")}</div>
          </div>
        `
        : "";
      const noteRow = data.notes
        ? `<div class="pg-chat-quote__notes">${this.escapeHtml(data.notes)}</div>`
        : "";
      return `
        <div class="pg-chat-quote">
          <div class="pg-chat-quote__eyebrow">Your quote summary</div>
          <div class="pg-chat-quote__title">${title}</div>
          <ul class="pg-chat-quote__rows">${rows}</ul>
          ${noteRow}
          ${priceBlock}
          <a class="pg-chat-quote__cta" href="${href}">
            Customize this product
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </a>
        </div>
      `;
    }

    // Render the "transfer to Jacob" card during business hours. Uses the
    // quote-card visual style so it reads as a primary action. The CTA is
    // a button (not a link) so the click handler can close our chat then
    // try to open Shopify Inbox's storefront widget.
    renderHumanHandoff(data) {
      if (!data) return "";
      const name = this.escapeHtml(data.name || "you");
      const email = data.email ? this.escapeHtml(data.email) : "";
      const phone = data.phone ? this.escapeHtml(data.phone) : "";
      const subRow = email
        ? `<div class="pg-chat-quote__notes">Jacob's been notified and has your details. Hop over to chat with him live now, or hang tight and he'll reach out at ${email}.</div>`
        : `<div class="pg-chat-quote__notes">Jacob's been notified. Tap below to chat with him live now.</div>`;
      return `
        <div class="pg-chat-quote">
          <div class="pg-chat-quote__eyebrow">Transferring to Jacob</div>
          <div class="pg-chat-quote__title">Got it, ${name}.</div>
          ${subRow}
          <button type="button" class="pg-chat-quote__cta" data-pg-open-inbox
                  data-pg-name="${name}" data-pg-email="${email}" data-pg-phone="${phone}">
            Chat with Jacob live
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
      `;
    }

    // Render the after-hours callback card (no Inbox button; Jacob isn't
    // in to receive Inbox messages). Customer just gets a confirmation
    // that their info is in.
    renderCallbackCard(data) {
      if (!data) return "";
      const name = this.escapeHtml(data.name || "you");
      const email = data.email ? this.escapeHtml(data.email) : "";
      const phone = data.phone ? this.escapeHtml(data.phone) : "";
      const reachVia = email && phone
        ? `${email} or ${phone}`
        : email || phone || "your contact info";
      const hours = this.escapeHtml(data.hours || "Mon–Fri, 8am to 4pm Eastern");
      return `
        <div class="pg-chat-quote">
          <div class="pg-chat-quote__eyebrow">After hours</div>
          <div class="pg-chat-quote__title">Got it, ${name}.</div>
          <div class="pg-chat-quote__notes">Jacob's out of the office right now. He'll reach out at ${reachVia} when he's back. Office hours are ${hours}.</div>
        </div>
      `;
    }

    formatText(s) {
      // Escape first so any literal <, >, & in the bot's text are safe.
      // Then layer markdown links, bare-path autolinks, and bare-URL
      // autolinks in a single regex pass — alternation order means
      // markdown wins where present, then path, then URL, no overlap.
      let html = this.escapeHtml(s || "");
      html = html.replace(
        /\[([^\]]+)\]\(([^)\s]+)\)|(^|[\s(])(\/[A-Za-z0-9][A-Za-z0-9_\-\/]*)(?=[\s.,;:)!?]|$)|(^|[\s(])(https?:\/\/[^\s<>)]+)/g,
        (match, mdText, mdUrl, pathLead, path, urlLead, url) => {
          if (mdText && mdUrl) {
            const safe = this.safeHref(mdUrl);
            if (!safe) return match;
            const target = /^https?:/i.test(safe) ? ' target="_blank" rel="noopener"' : "";
            return `<a href="${safe}" class="pg-chat-link"${target}>${mdText}</a>`;
          }
          if (path) {
            return `${pathLead}<a href="${path}" class="pg-chat-link">${path}</a>`;
          }
          if (url) {
            return `${urlLead}<a href="${url}" class="pg-chat-link" target="_blank" rel="noopener">${url}</a>`;
          }
          return match;
        },
      );
      return html.replace(/\n/g, "<br>");
    }

    // Allowlist for href values so a malformed model output can't
    // inject `javascript:` or `data:` URLs through the markdown link
    // syntax. Only same-origin paths, http(s), and mailto are allowed.
    safeHref(url) {
      if (!url) return null;
      if (url.startsWith("/")) return url;
      if (/^https?:\/\//i.test(url)) return url;
      if (/^mailto:/i.test(url)) return url;
      return null;
    }

    // Reveal + open Shopify Inbox. Inbox's storefront launcher appears
    // as #chat-button-container (modern app-block install) or one of
    // several legacy iframe selectors. We probe each, then fall back to
    // a toast. Removes the global hide class first so the launcher
    // becomes visible/clickable.
    // Fill Shopify Inbox's "Before we get started" form using the visitor
     // info we already collected from Dave, so the customer isn't asked twice.
     // Inbox's form is in a custom-element shadow DOM (inbox-online-store-chat)
     // and is React-based, so we have to use the native input setter to make
     // React notice the value change before dispatching the input event.
    fillInboxVisitorForm(visitor) {
      if (!visitor || (!visitor.name && !visitor.email && !visitor.phone)) return;
      const setNative = (el, value) => {
        const desc = Object.getOwnPropertyDescriptor(
          el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype,
          "value",
        );
        if (desc && desc.set) desc.set.call(el, value);
        else el.value = value;
        el.dispatchEvent(new Event("input", { bubbles: true }));
        el.dispatchEvent(new Event("change", { bubbles: true }));
      };
      const collectRoots = () => {
        const roots = [document];
        const host = document.querySelector("inbox-online-store-chat");
        if (host && host.shadowRoot) roots.push(host.shadowRoot);
        // Some Inbox versions wrap their form in nested shadow roots / iframes.
        document.querySelectorAll("iframe").forEach((f) => {
          try { if (f.contentDocument) roots.push(f.contentDocument); } catch (_) {}
        });
        return roots;
      };
      // Split a "First Last" string from Dave into the two fields Inbox uses.
      const fullName = (visitor.name || "").trim();
      const nameParts = fullName.split(/\s+/);
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "NA"; // Inbox requires last name; if customer only gave a first name, fall back to "NA"
      const findInput = (root, mustInclude, mustExclude, claimed) => {
        const inputs = root.querySelectorAll("input, textarea");
        for (const inp of inputs) {
          if (claimed && claimed.has(inp)) continue;
          const haystack = `${inp.type || ""} ${inp.name || ""} ${inp.id || ""} ${inp.placeholder || ""} ${inp.getAttribute("aria-label") || ""}`.toLowerCase();
          if (mustInclude.some((p) => haystack.includes(p))) {
            if (mustExclude && mustExclude.some((p) => haystack.includes(p))) continue;
            return inp;
          }
        }
        return null;
      };
      let filledOnce = false;
      const tryFill = () => {
        for (const root of collectRoots()) {
          const claimed = new Set();
          // Resolve in most-specific → least-specific order, claiming each
          // input as it's matched so the same field can't get filled twice.
          const emailInput = findInput(root, ["email"], ["name", "first", "last", "phone", "tel"], claimed);
          if (emailInput) claimed.add(emailInput);
          const phoneInput = findInput(root, ["phone", "tel"], ["email", "name"], claimed);
          if (phoneInput) claimed.add(phoneInput);
          const lastNameInput = findInput(root, ["last", "surname", "family"], ["email", "phone", "tel"], claimed);
          if (lastNameInput) claimed.add(lastNameInput);
          const firstNameInput = findInput(root, ["first", "given"], ["email", "phone", "tel", "last"], claimed);
          if (firstNameInput) claimed.add(firstNameInput);
          const genericNameInput = firstNameInput ? null
            : findInput(root, ["name"], ["last", "surname", "family", "user", "company", "business", "email", "phone", "tel"], claimed);
          if (genericNameInput) claimed.add(genericNameInput);

          let didFill = false;
          if (emailInput && visitor.email && emailInput.value !== visitor.email) {
            setNative(emailInput, visitor.email); didFill = true;
          }
          if (firstNameInput && firstName && firstNameInput.value !== firstName) {
            setNative(firstNameInput, firstName); didFill = true;
          }
          if (lastNameInput && lastName && lastNameInput.value !== lastName) {
            setNative(lastNameInput, lastName); didFill = true;
          }
          if (genericNameInput && fullName && genericNameInput.value !== fullName) {
            setNative(genericNameInput, fullName); didFill = true;
          }
          if (phoneInput && visitor.phone && phoneInput.value !== visitor.phone) {
            setNative(phoneInput, visitor.phone); didFill = true;
          }
          if (didFill) {
            filledOnce = true;
            // Try to submit the form so the customer goes straight to chat.
            // Look for a primary CTA: "Start chatting", "Continue", a [type=submit].
            setTimeout(() => {
              const submitBtn =
                root.querySelector('button[type="submit"]') ||
                Array.from(root.querySelectorAll("button")).find((b) => {
                  const t = (b.textContent || "").trim().toLowerCase();
                  return /start|continue|begin|chat|next|go/.test(t);
                });
              if (submitBtn && !submitBtn.disabled) submitBtn.click();
            }, 200);
            return true;
          }
        }
        return false;
      };
      // Poll for ~6 seconds — Inbox loads asynchronously and the form may
      // appear after the panel animation completes.
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (tryFill() || attempts > 24) clearInterval(interval);
      }, 250);
    }

    openShopifyInbox(visitor) {
      // Persist a session flag so on subsequent renders we keep our launcher
      // hidden and Inbox visible — the handoff is "sticky" until tab close,
      // which prevents both launchers from overlapping after the customer
      // wraps up with Inbox.
      try { sessionStorage.setItem("pg_chat_handed_off", "1"); } catch (_) {}
      document.documentElement.classList.remove("pg-inbox-hidden");
      document.documentElement.classList.add("pg-chat-handed-off");

      // Wipe any Shopify Inbox visitor data left over from earlier sessions
      // so it can't repopulate the form with stale values (e.g. test data).
      // Then seed with the current visitor — Inbox's actual storage key is
      // unknown to us, so this is a shotgun set across plausible names.
      try {
        Object.keys(localStorage).forEach((k) => {
          if (/shopify[-_]?chat|shopify[-_]?inbox|inbox[-_]?chat/i.test(k)) {
            localStorage.removeItem(k);
          }
        });
      } catch (_) {}
      if (visitor && (visitor.name || visitor.email || visitor.phone)) {
        const v = visitor;
        try {
          if (v.email) {
            localStorage.setItem("shopify-chat-email", v.email);
            localStorage.setItem("shopify_chat_email", v.email);
            localStorage.setItem("shopify-chat-customer-email", v.email);
            localStorage.setItem("__shopify_chat_email", v.email);
          }
          if (v.name) {
            localStorage.setItem("shopify-chat-name", v.name);
            localStorage.setItem("shopify_chat_name", v.name);
            localStorage.setItem("shopify-chat-customer-name", v.name);
          }
          if (v.phone) {
            localStorage.setItem("shopify-chat-phone", v.phone);
            localStorage.setItem("shopify_chat_phone", v.phone);
          }
          localStorage.setItem("shopify-chat-visitor", JSON.stringify({
            name: v.name || "", email: v.email || "", phone: v.phone || "",
          }));
          localStorage.setItem("shopify_chat_visitor", JSON.stringify({
            name: v.name || "", email: v.email || "", phone: v.phone || "",
          }));
        } catch (_) {}
      }
      const tryTargets = [
        () => typeof window.ShopifyChat?.open === "function" && (window.ShopifyChat.open(), true),
        () => {
          // Modern: <inbox-online-store-chat> custom element with shadow DOM.
          // Click the host — Inbox's internal click handler opens the panel.
          const host = document.querySelector("inbox-online-store-chat");
          if (!host) return false;
          // Try its shadow root for an actual button first.
          const shadow = host.shadowRoot;
          const btn =
            (shadow && (shadow.querySelector("button") || shadow.querySelector('[role="button"]'))) ||
            host;
          btn.click();
          return true;
        },
        () => {
          const el = document.querySelector("#ShopifyChat, [data-shopify-chat-trigger]");
          if (!el) return false;
          el.click();
          return true;
        },
        () => {
          const btn = document.querySelector(
            'button[aria-label*="chat" i]:not(.pg-chat-launcher), button[aria-label*="message" i]:not(.pg-chat-launcher), .ShopifyChatTriggerLauncher, [class*="ShopifyChat" i] button',
          );
          if (!btn) return false;
          btn.click();
          return true;
        },
      ];
      // Try once now; if Inbox loaded after our script, retry briefly.
      const attempt = () => {
        for (const tryIt of tryTargets) {
          try { if (tryIt()) return true; } catch (_) {}
        }
        return false;
      };
      const onOpened = () => this.fillInboxVisitorForm(visitor);
      if (attempt()) { onOpened(); return; }
      let tries = 0;
      const interval = setInterval(() => {
        tries += 1;
        if (attempt()) { clearInterval(interval); onOpened(); return; }
        if (tries >= 10) { clearInterval(interval); this.showInboxFallbackToast(); }
      }, 300);
    }

    showInboxFallbackToast() {
      let toast = document.querySelector(".pg-chat-inbox-toast");
      if (toast) toast.remove();
      toast = document.createElement("div");
      toast.className = "pg-chat-inbox-toast";
      toast.textContent = "Jacob has your details. Tap the chat icon at the bottom-right to message him directly.";
      document.body.appendChild(toast);
      requestAnimationFrame(() => toast.classList.add("pg-chat-inbox-toast--visible"));
      setTimeout(() => {
        toast.classList.remove("pg-chat-inbox-toast--visible");
        setTimeout(() => toast.remove(), 300);
      }, 6000);
    }

    /* ---------------- smart scroll + new-message pill ---------------- */

    isNearBottom() {
      const el = this.messagesEl;
      if (!el) return true;
      return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    }

    handleScroll() {
      if (!this.messagesEl) return;
      const wasAtBottom = this.userAtBottom;
      this.userAtBottom = this.isNearBottom();
      if (this.userAtBottom && !wasAtBottom) {
        // They scrolled themselves back to the bottom — tear down the pill
        // because there's nothing unread anymore.
        this.unseenCount = 0;
        this.hideNewMessagePill();
      }
    }

    showNewMessagePill() {
      if (!this.newMsgBtn) return;
      this.newMsgBtn.removeAttribute("hidden");
      this.newMsgLabel.textContent =
        this.unseenCount > 1 ? `${this.unseenCount} new messages` : "New message";
      // rAF so the just-removed `hidden` attribute lets the transition fire.
      requestAnimationFrame(() => this.newMsgBtn.classList.add("pg-chat-newmsg--visible"));
    }

    hideNewMessagePill() {
      if (!this.newMsgBtn) return;
      this.unseenCount = 0;
      this.newMsgBtn.classList.remove("pg-chat-newmsg--visible");
      // Wait for the fade-out before re-hiding so the transition plays.
      setTimeout(() => {
        if (!this.newMsgBtn.classList.contains("pg-chat-newmsg--visible")) {
          this.newMsgBtn.setAttribute("hidden", "");
        }
      }, 200);
    }

    /**
     * Decide whether to auto-scroll for a new bubble. Forced calls (e.g.
     * the customer just sent a message) always scroll. Otherwise we only
     * scroll if the customer is already at the bottom — preserving their
     * place if they're scrolled up reading a previous message.
     *
     * `silent: true` updates scroll without surfacing the pill; used during
     * the streaming typing-bubble lifecycle so we don't flash "New message"
     * for what's just the typing indicator becoming a real bubble.
     */
    scrollToBottom(opts) {
      const o = opts || {};
      if (!this.messagesEl) return;
      if (o.force || this.userAtBottom) {
        this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
        this.userAtBottom = true;
        if (o.force) this.hideNewMessagePill();
        return;
      }
      if (!o.silent) {
        this.unseenCount += 1;
        this.showNewMessagePill();
      }
    }

    escapeHtml(s) {
      return String(s == null ? "" : s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    }

    escapeAttr(s) {
      return this.escapeHtml(s).replace(/"/g, "&quot;");
    }
  }

  function sleep(ms) {
    return new Promise((r) => setTimeout(r, ms));
  }

  function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
      reader.onerror = () => reject(reader.error || new Error("FileReader failed"));
      reader.readAsDataURL(file);
    });
  }

  if (!customElements.get("pg-chat-assistant")) {
    customElements.define("pg-chat-assistant", PgChatAssistant);
  }
})();
